import type { DashboardPortfolioReport } from '../types';

/**
 * Shared action-queue rules for Today's Dashboard (v1 Action Queue, v2 Drift
 * Queue, Needs Action snapshot card, and status bar). Keep all surfaces in sync
 * by importing from this module only.
 */

const isUrgentActionQueueReport = (report: DashboardPortfolioReport): boolean =>
  Boolean(
    report.liquidateRebalanceRequired || report.transferRebalanceRequired,
  );

const isModerateActionQueueReport = (
  report: DashboardPortfolioReport,
  urgentIds?: Set<string>,
): boolean => {
  if (urgentIds?.has(report.id)) return false;
  if (isUrgentActionQueueReport(report)) return false;

  if (report.unverifiedSubTradeRequests) return true;
  if (
    report.driftRebalanceRequired &&
    !report.pendingSubTradeRequests &&
    (report.marketValueCents || 0) > 0
  ) {
    return true;
  }
  return false;
};

/** Same inclusion rule as Drift Queue rows and the "Needs Action" stat filter. */
export const isActionQueueReport = (
  report: DashboardPortfolioReport,
): boolean =>
  isUrgentActionQueueReport(report) || isModerateActionQueueReport(report);

export type ActionQueuePartition = {
  urgent: DashboardPortfolioReport[];
  moderate: DashboardPortfolioReport[];
};

export const partitionActionQueueReports = (
  reports: DashboardPortfolioReport[],
): ActionQueuePartition => {
  const urgent = reports.filter(isUrgentActionQueueReport);
  const urgentIds = new Set(urgent.map((p) => p.id));
  const moderate = reports.filter((p) =>
    isModerateActionQueueReport(p, urgentIds),
  );
  return { urgent, moderate };
};

export type PortfolioDashboardStats = {
  urgent: number;
  moderate: number;
  actionQueue: number;
  onTrack: number;
  total: number;
  totalValue: number;
  actionQueueValue: number;
};

export type PortfolioDashboardData = {
  stats: PortfolioDashboardStats;
  actionQueue: ActionQueuePartition;
};

const getValue = (report: DashboardPortfolioReport[]): number => {
  return report.reduce((sum, p) => sum + (p.marketValueCents || 0), 0);
};

export const buildPortfolioDashboardData = (
  reports: DashboardPortfolioReport[],
): PortfolioDashboardData => {
  const actionQueue = partitionActionQueueReports(reports);
  const { urgent, moderate } = actionQueue;

  const onTrack = reports.filter(
    (p) =>
      !isActionQueueReport(p) &&
      !p.driftRebalanceRequired &&
      !p.liquidateRebalanceRequired &&
      !p.transferRebalanceRequired &&
      (p.pendingSubTradeRequests || 0) === 0,
  ).length;

  const totalValue = getValue(reports);

  const actionQueueValue = getValue([...urgent, ...moderate]);

  return {
    actionQueue,
    stats: {
      urgent: urgent.length,
      moderate: moderate.length,
      actionQueue: urgent.length + moderate.length,
      onTrack,
      total: reports.length,
      totalValue,
      actionQueueValue,
    },
  };
};

export const computePortfolioDashboardStats = (
  reports: DashboardPortfolioReport[],
): PortfolioDashboardStats => buildPortfolioDashboardData(reports).stats;
